import { create } from "zustand";
import { Course, CoursesState, GetCoursesParams } from "../types/api";
import {
  addLearningCourse,
  changeCourseStatus,
  getCourses,
  getCoursesFilterOptions,
  getLearningCourses,
  getRecommendedCourses,
  removeLearningCourse,
} from "../api/coursesApi";
import { devtools } from "zustand/middleware";

const useCoursesStore = create<CoursesState>()(
  devtools(
    (set, get) => ({
      courses: [],
      currentCourses: [],
      filters: {},
      currentPage: 1,
      currentTab: "Recommended",
      pageInfo: {
        count: null,
        next: null,
        previous: null,
      },
      recommendedCourses: { count: null, results: [] },
      learningCourses: { count: null, results: [] },
      loading: false,
      error: null,

      fetchCourses: async (credentials: GetCoursesParams) => {
        set({ loading: true, error: null });
        try {
          const response = await getCourses(credentials);
          if (!response) {
            throw new Error("Failed to fetch courses");
          }

          const data: Course[] = response.data.results;
          set({
            courses: data,
            pageInfo: {
              count: response.data.count,
              next: response.data.next,
              previous: response.data.previous,
            },
            loading: false,
          });
          return data;
        } catch (error: any) {
          set({ error: error.message, loading: false });
          return error;
        }
      },

      fetchRecommendedCourses: async (credentials: GetCoursesParams) => {
        set({ loading: true, error: null });
        try {
          const response = await getRecommendedCourses(credentials);
          if (!response) {
            throw new Error("Failed to fetch recommended courses");
          }
          const data: Course[] = response.data.recommendation.results;
          set({
            recommendedCourses: {
              results: data,
              count: response.data.recommendation.count,
            },
            loading: false,
          });
          return data;
        } catch (error: any) {
          set({ error: error.message, loading: false });
          return error;
        }
      },

      fetchLearningCourses: async (credentials: GetCoursesParams) => {
        set({ loading: true, error: null });
        try {
          const response = await getLearningCourses(credentials);
          if (!response) {
            throw new Error("Failed to fetch learning courses");
          }
          set({
            learningCourses: {
              results: response.data.results,
              count: response.data.count,
            },
            loading: false,
          });
          if (get().currentTab === "My learning") {
            set({
              currentCourses: response.data.results.map((item) => item.course),
            });
          }
          return response.data.results.map((item) => item.course);
        } catch (error: any) {
          set({ error: error.message, loading: false });
          throw error;
        }
      },

      setCurrentCourses: (courses: Course[]) => {
        set({ currentCourses: courses });
      },

      setCurrentPage: (page: number) => {
        set({ currentPage: page });
      },

      setCurrentTab: (tab: string) => {
        set({ currentTab: tab });
      },

      addLearningCourses: async (
        courseId: number,
        credentials?: GetCoursesParams
      ) => {
        set({ loading: true, error: null });
        try {
          const response = await addLearningCourse({ courseId });
          if (!response) {
            throw new Error("Failed to add course to learning");
          }

          const updateCourses = (coursesArray: Course[]) => {
            return coursesArray.map((course) =>
              course.id === courseId
                ? { ...course, is_in_learnings: true }
                : course
            );
          };

          set((state) => {
            const updatedRecommendedCourses = {
              ...state.recommendedCourses,
              results: updateCourses(state.recommendedCourses.results),
            };

            const updatedCourses = updateCourses(state.courses);
            const updatedCurrentCourses = updateCourses(state.currentCourses);

            return {
              recommendedCourses: updatedRecommendedCourses,
              courses: updatedCourses,
              currentCourses: updatedCurrentCourses,
              loading: false,
            };
          });

          await get().fetchLearningCourses(credentials);
        } catch (error: any) {
          set({ error: error.message, loading: false });
        }
      },

      removeLearningCourse: async (
        deleteId: number,
        courseId: number,
        credentials: GetCoursesParams
      ) => {
        set({ loading: true, error: null });
        try {
          const response = await removeLearningCourse({ courseId: deleteId });
          if (!response) {
            throw new Error("Failed to remove course from learning");
          }

          const updateCourses = (coursesArray: Course[]) => {
            return coursesArray.map((course) =>
              course.id === courseId
                ? { ...course, is_in_learnings: false }
                : course
            );
          };

          set((state) => {
            const updatedRecommendedCourses = {
              ...state.recommendedCourses,
              results: updateCourses(state.recommendedCourses.results),
            };

            const updatedCourses = updateCourses(state.courses);
            const updatedCurrentCourses = updateCourses(state.currentCourses);

            return {
              recommendedCourses: updatedRecommendedCourses,
              courses: updatedCourses,
              currentCourses: updatedCurrentCourses,
              loading: false,
            };
          });

          await get().fetchLearningCourses(credentials);
        } catch (error: any) {
          set({ error: error.message, loading: false });
        }
      },

      fetchFilter: async () => {
        try {
          const filter = await getCoursesFilterOptions();

          set({ filters: filter.data });
        } catch (error: any) {
          set({ error: error.message, loading: false });
        }
      },

      changeLearningCourseStatus: async (courseId, status) => {
        try {
          const updatedData = await changeCourseStatus({ courseId, status });

          set((state) => ({
            learningCourses: {
              ...state.learningCourses,
              results: state.learningCourses.results.map((learningCourse) =>
                learningCourse.id === courseId
                  ? {
                      ...learningCourse,
                      status: updatedData.data.status,
                    }
                  : learningCourse
              ),
            },
          }));
        } catch (error) {
          console.error("Failed to change learning course status:", error);
        }
      },

      resetRecommendedCourses: () => {
        set({
          recommendedCourses: { count: null, results: [] },
        });
      },

      resetCoursesState: () => {
        set({
          courses: [],
          currentCourses: [],
          currentPage: 1,
          currentTab: "Recommended",
          pageInfo: {
            count: null,
            next: null,
            previous: null,
          },
          recommendedCourses: { count: null, results: [] },
          learningCourses: { count: null, results: [] },
          loading: false,
          error: null,
        });
      },
    }),
    { name: "coursesStore" }
  )
);

export default useCoursesStore;
